import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap, tap, withLatestFrom, filter, merge, debounceTime } from 'rxjs';
import { odataParamsFactory } from '@tdw/core';
import { Store } from '@ngrx/store';
import * as fromPigSig from '../fromPigSig.barrel';
import { PigSigService } from 'src/app/services/pigsig.service';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class PigSigEffects {
  currentDate = new Date().toISOString();

  constructor(
    private store: Store<fromPigSig.State>,
    private actions$: Actions,
    private pigsigService: PigSigService) { }


  loadLookups$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadLookups),
    switchMap(action => merge(
      of(fromPigSig.loadIndicators()),
      of(fromPigSig.loadConnections()),
      of(fromPigSig.loadFlangeMatls()),
      of(fromPigSig.loadShafts()),
      of(fromPigSig.loadPlugs()),
      of(fromPigSig.loadOrings()),
      of(fromPigSig.loadDiameters()),
      of(fromPigSig.loadThicknesses()),
      of(fromPigSig.loadSpareParts())
    )),
    catchError(error => of(fromPigSig.loadLookupsFail({ error })))));

  updateLookup$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadIndicatorsSuccess), map(action => fromPigSig.updatePigSigLookups({ value: { indicators: action.indicators } }))
  ))

  updateConnections$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadConnectionsSuccess), map(action => fromPigSig.updatePigSigLookups({ value: { connections: action.connections } }))
  ))

  updateFlangeMatls$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadFlangeMatlsSuccess), map(action => fromPigSig.updatePigSigLookups({ value: { flangeMatls: action.flangeMatls } }))
  ));

  updateShafts$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadShaftsSuccess), map(action => fromPigSig.updatePigSigLookups({ value: { shafts: action.shafts } }))
  ));

  updatePlugs$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadPlugsSuccess), map(action => fromPigSig.updatePigSigLookups({ value: { plugs: action.plugs } }))
  ));

  updateOrings$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadOringsSuccess), map(action => fromPigSig.updatePigSigLookups({ value: { orings: action.orings } }))
  ));

  updateDiameters$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadDiametersSuccess), map(action => fromPigSig.updatePigSigLookups({ value: { diameters: action.diameters } }))
  ))

  updateWallThicknesses$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadThicknessesSuccess), map(action => fromPigSig.updatePigSigLookups({ value: { wallThicknesses: action.wallThicknesses } }))
  ));

  updateSpareParts$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadSparePartsSuccess), map(action => fromPigSig.updatePigSigLookups({ value: { spareParts: action.spareParts } }))
  ));

  updateFilter$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.updatePigSigFilter),
    map(action => fromPigSig.loadPigSigResults())
  ));

  loadPigSigResults = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadPigSigResults),
    withLatestFrom(this.store.select(fromPigSig.selectActiveFilter)),
    filter(([action, q]) => !!q.connectionID && !!q.indicatorID),
    switchMap(([action, q]) => this.pigsigService.getFilterResult(q)
      .pipe(map(res => fromPigSig.loadPigSigResultsSuccess({ pigSigResults: res })),
        catchError(error => of(fromPigSig.loadPigSigResultsFail({ error })))))));

  //load the flange heights so we have the number to put in the partnumber.
  loadPigSigFlangeHeights$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.updatePigSigFilter),
    withLatestFrom(this.store.select(fromPigSig.selectActiveFilter), this.store.select(fromPigSig.selectActiveConnection)),
    filter(([action, activeFilter, connection]) => (activeFilter.connectionID || -1) > 1 && !!connection),
    switchMap(([action, activeFilter, connection]) => this.pigsigService.getFlangeHeights(odataParamsFactory({ filter: `PipeSizePNVal eq '${connection?.partNumberValue}'` }))
      .pipe(map(res => fromPigSig.loadFlangeHeightsSuccess({ flangeHeights: res.value || [] })),
        catchError(error => of(fromPigSig.loadFlangeHeightsFail({ error }))))
    )
  ));

  removeLoaderIfMinNotMet$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadPigSigResults),
    withLatestFrom(this.store.select(fromPigSig.selectActiveFilter)),
    filter(([action, q]) => !q.connectionID || !q.indicatorID),
    map(action => fromPigSig.removeLoader({ loader: fromPigSig.Loaders.loadPigSigResults }))
  ));

  updateFlangeHeightWithDebounce$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.updateFlangeHeightChange),
    debounceTime(600),
    map(action => fromPigSig.updatePigSigFilter({ value: { flangeHeight: action.val } }))
  ));

  loadIndicators$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadIndicators),
    switchMap(action => this.pigsigService.getIndicators(odataParamsFactory({ filter: `inactivedate eq null OR inactivedate gt ${this.currentDate}`, orderBy: 'SortOrder' }))
      .pipe(map(res => fromPigSig.loadIndicatorsSuccess({ indicators: res.value || [] })),
        catchError(error => of(fromPigSig.loadIndicatorsFail({ error })))))));

  loadShafts$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadShafts),
    switchMap(action => this.pigsigService.getShafts(odataParamsFactory({ filter: `inactivedate eq null OR inactivedate gt ${this.currentDate}`, orderBy: 'SortOrder' }))
      .pipe(map(res => fromPigSig.loadShaftsSuccess({ shafts: res.value || [] })),
        catchError(error => of(fromPigSig.loadShaftsFail({ error })))))));

  loadPlugs$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadPlugs),
    switchMap(action => this.pigsigService.getPlugs(odataParamsFactory({ filter: `inactivedate eq null OR inactivedate gt ${this.currentDate}`, orderBy: 'SortOrder' }))
      .pipe(map(res => fromPigSig.loadPlugsSuccess({ plugs: res.value || [] })),
        catchError(error => of(fromPigSig.loadPlugsFail({ error })))))));

  loadOrings$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadOrings),
    switchMap(action => this.pigsigService.getOrings(odataParamsFactory({ filter: `inactivedate eq null OR inactivedate gt ${this.currentDate}`, orderBy: 'SortOrder' }))
      .pipe(map(res => fromPigSig.loadOringsSuccess({ orings: res.value || [] })),
        catchError(error => of(fromPigSig.loadOringsFail({ error })))))));

  loadConnections$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadConnections),
    switchMap(action => this.pigsigService.getConnections(odataParamsFactory({ filter: `inactivedate eq null OR inactivedate gt ${this.currentDate}`, orderBy: 'SortOrder' }))
      .pipe(map(res => fromPigSig.loadConnectionsSuccess({ connections: res.value || [] })),
        catchError(error => of(fromPigSig.loadConnectionsFail({ error })))))));

  loadFlangeMatls$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadFlangeMatls),
    switchMap(action => this.pigsigService.getFlangeMatls(odataParamsFactory({ filter: `inactivedate eq null OR inactivedate gt ${this.currentDate}`, orderBy: 'SortOrder' }))
      .pipe(map(res => fromPigSig.loadFlangeMatlsSuccess({ flangeMatls: res.value || [] })),
        catchError(error => of(fromPigSig.loadFlangeMatlsFail({ error })))))));

  loadSpareParts$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadSpareParts),
    switchMap(action => this.pigsigService.getSpareParts(odataParamsFactory({ filter: `inactivedate eq null OR inactivedate gt ${this.currentDate}` }))
      .pipe(map(res => fromPigSig.loadSparePartsSuccess({ spareParts: res.value || [] })),
        catchError(error => of(fromPigSig.loadSparePartsFail({ error })))))));

  loadDiameters$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadDiameters),
    switchMap(action => this.pigsigService.getDiameters(odataParamsFactory({ filter: `inactivedate eq null OR inactivedate gt ${this.currentDate}`, orderBy: 'SortOrder' }))
      .pipe(map(res => fromPigSig.loadDiametersSuccess({ diameters: res.value || [] })),
        catchError(error => of(fromPigSig.loadDiametersFail({ error })))))));

  loadThicknesses$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadThicknesses),
    switchMap(action => this.pigsigService.getThicknesses()
      .pipe(map(res => fromPigSig.loadThicknessesSuccess({ wallThicknesses: res.value || [] })),
        catchError(error => of(fromPigSig.loadThicknessesFail({ error })))))));

  downloadPigSigDrawing$ = createEffect(() => this.actions$.pipe(
    ofType(fromPigSig.loadDwg),
    tap(action => console.log('download dwg')),
    switchMap(action => this.pigsigService.getFlatterFilesDrawingByPartNumber(action.filename)
      .pipe(
        map(file => {
          window.open(window.URL.createObjectURL(file));
          return fromPigSig.loadDwgSuccess();
        }))),
    catchError(error => of(fromPigSig.loadDwgFail({ error })))
  ));
}


